import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import useToggle from 'next/hooks/use-toggle';
import Dropdown from './Dropdown';
import Checkbox, { CheckboxGroup } from './Checkbox';
import { SquareButton, ButtonLink } from './Button';
import Icon from './Icon';
import { H4 } from './Heading';

const OpenerWrapper = styled.div`
  position: relative;
  width: fit-content;
`;

const FilterButton = styled(SquareButton)`
  ${Icon} {
    color: var(--icon);
  }

  ${({ active }) =>
    active &&
    `
      border-color: var(--primary);

      ${Icon} {
        color: var(--primary);
      }
    `}
`;

const GroupName = styled(H4)``;

const FilterWrapper = styled.div`
  margin: -8px;
  min-width: 250px;
  position: relative;

  ${CheckboxGroup} {
    border-bottom: 1px solid var(--ui-vertical-separator);
    padding: 20px 20px 8px;

    ${Checkbox} {
      margin-bottom: 16px;
      padding: 0;
      min-height: auto;

      &:hover {
        background-color: transparent;
      }
    }

    ${GroupName} {
      color: var(--heading-primary);
      font-weight: var(--bold);
      font-size: var(--regular);
      margin-bottom: 16px;
    }
  }
`;

const Badge = styled.span`
  align-items: center;
  border-radius: 50%;
  background-color: var(--primary);
  color: var(--white);
  display: flex;
  font-size: var(--xx-small);
  height: 16px;
  justify-content: center;
  position: absolute;
  right: -4px;
  top: -4px;
  width: 16px;
`;

const ClearButton = styled(ButtonLink)`
  position: absolute;
  right: 24px;
  top: 22px;
`;

function Filter({ filters = {}, onClear, onChange }) {
  const [isOpen, toggleOpen] = useToggle(false);
  const enabledFiltersCount = Object.values(filters)
    .flatMap(filter => Object.values(filter?.options))
    .reduce((acc, filter) => (filter?.enabled ? acc + 1 : acc), 0);

  return (
    <Dropdown
      onShow={toggleOpen}
      onHide={toggleOpen}
      attachment={
        <FilterWrapper>
          {enabledFiltersCount > 0 && (
            <ClearButton aria-label="clear filters" onClick={onClear}>
              Clear
            </ClearButton>
          )}
          {Object.entries(filters).map(
            ([groupKey, { name, options }], groupIndex) => (
              <CheckboxGroup key={groupKey}>
                <GroupName>{name}</GroupName>
                {Object.entries(options).map(
                  (
                    [optionKey, { label, enabled, checkboxProps }],
                    optionIndex
                  ) => {
                    return (
                      <Checkbox
                        aria-label={`filter by ${label}`}
                        key={optionKey}
                        checked={enabled}
                        autoFocus={groupIndex === 0 && optionIndex === 0}
                        onChange={isChecked => {
                          onChange(groupKey, optionKey, isChecked);
                        }}
                        {...checkboxProps}
                      >
                        {label}
                      </Checkbox>
                    );
                  }
                )}
              </CheckboxGroup>
            )
          )}
        </FilterWrapper>
      }
    >
      <OpenerWrapper>
        {enabledFiltersCount > 0 && !isOpen && (
          <Badge
            aria-label={`Number of selected filters: ${enabledFiltersCount}`}
          >
            {enabledFiltersCount}
          </Badge>
        )}
        <FilterButton
          aria-label="Open filter menu"
          icon="sliders-h"
          kind="secondary"
          active={isOpen}
        />
      </OpenerWrapper>
    </Dropdown>
  );
}

Filter.displayName = 'Filter';

Filter.propTypes = {
  filters: PropTypes.objectOf(
    PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
      options: PropTypes.objectOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.string,
          enabled: PropTypes.bool,
        })
      ),
    })
  ),
  onClear: PropTypes.func,
  onChange: PropTypes.func,
};

export default styled(Filter)``;
